import type { FormScope, FormVersion } from "../state/formVersion";
import { useFormVersionState } from "../state/formVersion";
import { initialized } from "../utils/initializedUtil";
import FieldId from "../fields/FieldId";
import { sanitizeDataName } from "../utils/dataNameUtil";
import { isEmpty, isEqual, isNil } from "lodash-es";
import { equalsCaseInsensitive } from "../utils/stringUtil";
import VariableId from "../variables/VariableId";
import type SubformId from "../state/SubformId";
import { Scope } from "../types";
import type { FieldConfig, FieldDataNameProperties } from "../fields/types";

let formVersionStoreInstance: FormVersion | null = null;

const init = () => {
  formVersionStoreInstance = useFormVersionState();
};

const formVersionStore = () => initialized(formVersionStoreInstance, "formVersionStore");

type DataNameServiceType = {
  init: () => void;
  getValidDataNameForField: (dataName: string, id?: FieldId, scope?: FormScope) => string;
  getValidDataNameForVariable: (dataName: string, id?: VariableId) => string;
};

// Directly get fields from store instead of using the FieldService, to prevent circular dependencies
const getFields = () => formVersionStore().fields;

// Directly get variables from store instead of using the VariableService, to prevent circular dependencies
const getVariables = () => formVersionStore().variables;

const getValidDataNameForField = (dataName: string, id: FieldId | undefined, scope: FormScope = Scope.ROOT) =>
  getValidDataName(dataName, id, scope);

const getValidDataNameForVariable = (dataName: string, id: VariableId | undefined) =>
  getValidDataName(dataName, id, Scope.GLOBAL);

const getValidDataName = (
  dataName: string,
  id: FieldId | VariableId | undefined,
  scope: SubformId | Scope,
  suffixCount?: number,
): string => {
  const sanitizedDataName = sanitizeDataName(dataName);
  const newDataName = !isNil(suffixCount) ? `${sanitizedDataName}${suffixCount}` : sanitizedDataName;
  return (doesNotExistAsField(newDataName, id, scope) && doesNotExistAsVariable(newDataName, id)) ||
    isEmpty(newDataName)
    ? newDataName
    : getValidDataName(dataName, id, scope, !isNil(suffixCount) ? suffixCount + 1 : 1);
};

const hasDataNameProperty = (
  field: FieldConfig<FieldDataNameProperties>,
): field is FieldConfig<Required<FieldDataNameProperties>> => !isNil(field.properties.data_name);

const doesNotExistAsField = (dataName: string, id: FieldId | VariableId | undefined, scope: SubformId | Scope) =>
  getFields()
    .filter((field) => scope === Scope.GLOBAL || isEqual(field.scope, scope))
    .filter((field) => (id instanceof FieldId ? !field.uid.equals(id) : true))
    .filter(hasDataNameProperty)
    .every((field) => !equalsCaseInsensitive(dataName, field.properties.data_name));

const doesNotExistAsVariable = (dataName: string, id: VariableId | FieldId | undefined) =>
  getVariables()
    .filter((variable) => (id instanceof VariableId ? !variable.uid.equals(id) : true))
    .every((variable) => !equalsCaseInsensitive(dataName, variable.dataName));

export default {
  init,
  getValidDataNameForField,
  getValidDataNameForVariable,
} as DataNameServiceType;
